
import React from 'react';
import { Newspaper, Tent, Route, FolderOpen, Compass, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { SidebarMode } from '../../types/sidebar';

interface MobileBottomNavProps {
  mobileView: 'map' | 'list';
  sidebarMode: SidebarMode;
  spotsDisabled?: boolean;
  onSetMobileView: (view: 'map' | 'list') => void;
  onSetSidebarMode: (mode: SidebarMode) => void;
  onOpenCopilot?: () => void;
  copilotEnabled?: boolean;
  /** Uleste nyheter → tall-badge på «Nytt» (speiler desktop-railen). */
  newsCount?: number;
  /** Samarbeidsmapper m/ usett innhold/chat fra andre → tall-badge på «Mitt». */
  myContentCount?: number;
}

/** Tall-badge på et bunnmeny-ikon (speiler SidebarRail-badgen). */
const NavBadge: React.FC<{ count: number; color: string; text: string }> = ({ count, color, text }) => (
  <span className={`absolute top-1 right-1/2 translate-x-4 min-w-[16px] h-[16px] rounded-full ${color} border-2 border-[var(--bg-surface-alt)] flex items-center justify-center px-0.5`}>
    <span className={`text-3xs font-black leading-none ${text}`}>{count > 99 ? '99+' : count}</span>
  </span>
);

const MobileBottomNav: React.FC<MobileBottomNavProps> = ({
  mobileView,
  sidebarMode,
  spotsDisabled,
  onSetMobileView,
  onSetSidebarMode,
  onOpenCopilot,
  copilotEnabled = false,
  newsCount = 0,
  myContentCount = 0,
}) => {
  const { t } = useTranslation();

  const tap = () => { try { navigator.vibrate?.(8); } catch {} };

  const isDiscoverActive = mobileView === 'list' && sidebarMode === 'discover';
  const isNewsActive = mobileView === 'list' && sidebarMode === 'news';
  const isSpotsActive = mobileView === 'list' && sidebarMode === 'spots';
  const isRoutesActive = mobileView === 'list' && sidebarMode === 'routes';
  const isMyContentActive = mobileView === 'list' && sidebarMode === 'mycontent';

  return (
    <div className={`sm:hidden absolute left-0 right-0 bottom-0 z-sticky grid ${copilotEnabled && onOpenCopilot ? 'grid-cols-6' : 'grid-cols-5'} gap-1 px-2 pt-1 glass border-t border-[var(--border-primary)] rounded-t-2xl shadow-2xl animate-in slide-in-from-bottom-4`} style={{ paddingBottom: 'calc(0.25rem + env(safe-area-inset-bottom, 0px))' }}>
      {/* Oppdag */}
      <button
        onClick={() => { tap(); if (isDiscoverActive) { onSetMobileView('map'); } else { onSetSidebarMode('discover'); onSetMobileView('list'); } }}
        className={`min-w-0 flex flex-col items-center gap-0.5 px-1 py-2.5 rounded-xl min-h-[44px] transition ${isDiscoverActive ? 'bg-brand text-stone-900 shadow-lg' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)]'
          }`}
      >
        <Compass size={18} />
        <span className="text-2xs font-semibold uppercase tracking-wide">{t('nav.discover')}</span>
      </button>

      {/* Nyheter */}
      <button
        onClick={() => { tap(); if (isNewsActive) { onSetMobileView('map'); } else { onSetSidebarMode('news'); onSetMobileView('list'); } }}
        className={`relative min-w-0 flex flex-col items-center gap-0.5 px-1 py-2.5 rounded-xl min-h-[44px] transition ${isNewsActive ? 'bg-brand text-stone-900 shadow-lg' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)]'
          }`}
      >
        <Newspaper size={18} />
        <span className="text-2xs font-semibold uppercase tracking-wide">{t('nav.news', 'Nytt')}</span>
        {newsCount > 0 && !isNewsActive && <NavBadge count={newsCount} color="bg-amber-500" text="text-stone-900" />}
      </button>

      {/* Plasser */}
      <button
        disabled={spotsDisabled}
        onClick={() => { if (spotsDisabled) return; tap(); if (isSpotsActive) { onSetMobileView('map'); } else { onSetSidebarMode('spots'); onSetMobileView('list'); } }}
        className={`min-w-0 flex flex-col items-center gap-0.5 px-1 py-2.5 rounded-xl min-h-[44px] transition ${spotsDisabled ? 'opacity-30 cursor-not-allowed' : ''} ${isSpotsActive && !spotsDisabled ? 'bg-brand text-stone-900 shadow-lg' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)]'
          }`}
      >
        <Tent size={18} />
        <span className="text-2xs font-semibold uppercase tracking-wide">{t('nav.spots')}</span>
      </button>

      {/* Ruter */}
      <button
        onClick={() => { tap(); if (isRoutesActive) { onSetMobileView('map'); } else { onSetSidebarMode('routes'); onSetMobileView('list'); } }}
        className={`min-w-0 flex flex-col items-center gap-0.5 px-1 py-2.5 rounded-xl min-h-[44px] transition ${isRoutesActive ? 'bg-brand text-stone-900 shadow-lg' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)]'
          }`}
      >
        <Route size={18} />
        <span className="text-2xs font-semibold uppercase tracking-wide">{t('nav.routes')}</span>
      </button>

      {/* Mitt innhold */}
      <button
        onClick={() => { tap(); if (isMyContentActive) { onSetMobileView('map'); } else { onSetSidebarMode('mycontent'); onSetMobileView('list'); } }}
        className={`relative min-w-0 flex flex-col items-center gap-0.5 px-1 py-2.5 rounded-xl min-h-[44px] transition ${isMyContentActive ? 'bg-brand text-stone-900 shadow-lg' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)]'
          }`}
      >
        <FolderOpen size={18} />
        <span className="text-2xs font-semibold uppercase tracking-wide">{t('nav.mycontent', 'Mitt')}</span>
        {myContentCount > 0 && !isMyContentActive && <NavBadge count={myContentCount} color="bg-sky-500" text="text-white" />}
      </button>

      {/* Reise-copilot — testfunksjon, kun admin/testere */}
      {copilotEnabled && onOpenCopilot && (
        <button
          onClick={() => { tap(); onOpenCopilot(); }}
          className="min-w-0 flex flex-col items-center gap-0.5 px-1 py-2.5 rounded-xl min-h-[44px] text-brand transition hover:bg-brand/10"
        >
          <Sparkles size={18} />
          <span className="text-2xs font-semibold uppercase tracking-wide">{t('nav.copilot', 'Copilot')}</span>
        </button>
      )}
    </div>
  );
};

export default React.memo(MobileBottomNav);
